import React, { useState } from 'react';
import * as yup from 'yup';
import { FormikHelpers } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { reqEmail, reqObject, reqString } from '../../data/validations.ts';
import {
  ageCategories,
  civilStatusCategories,
  genderCategories,
  responseCategories,
} from '../../data/comboCategories.ts';
import XForm from '../../components/forms/XForm.tsx';
import XTextInput from '../../components/inputs/XTextInput.tsx';
//import XPhone from '../../components/inputs/XPhone.tsx';
import XSelectInput from '../../components/inputs/XSelectInput.tsx';
import { hasValue, toOptions } from '../../components/inputs/inputHelpers.tsx';

import { remoteRoutes } from '../../data/constants.ts';
import { post } from '../../utils/ajax.ts';
import Toast from '../../utils/Toast.ts';
import XRadioInput from '../../components/inputs/XRadioInput.tsx';
import { XRemoteSelect } from '../../components/inputs/XRemoteSelect.tsx';
import { getDayList, getMonthsList } from '../../utils/dateHelpers.ts';
import { ICreatePersonDto } from '../contacts/types';
import { XMapsInput } from '../../components/inputs/XMapsInput.tsx';
import { parseGooglePlace } from '../../components/plain-inputs/PMapsInput.tsx';
import XRemoteSelectLoadOnOpen from '../../components/inputs/XRemoteSelectLoadOnOpen.tsx';
import XPhone from '../../components/inputs/XPhone.tsx';
import "../../utils/lang/i18n";
import { useTranslation } from 'react-i18next';


interface IProps {
  data: any | null;
  done?: () => any;
}

const schema = yup.object().shape({
  churchName: reqString,
  firstName: reqString,
  otherNames: reqString,
  gender: reqString,
  birthDay: reqString,
  birthMonth: reqString,
  civilStatus: reqString,
  ageGroup: reqString,
  churchLocation: reqObject,
  email: reqEmail,
  phone: reqString,
  inCell: reqString,
});

const initialValues = {
  churchName: 'Conectados',
  firstName: '',
  middleName: '',
  lastName: '',
  birthDay: '',
  birthMonth: '',
  gender: '',
  civilStatus: '',
  ageGroup: '',
  placeOfWork: '',
  residence: '',
  cellGroup: null,
  churchLocation: null,
  email: '',
  phone: '',
  inCell: '',
  joinCell: '',
};

const processName = (name: string): string[] => {
  const [lastName, ...otherNames] = name.split(' ');
  if (hasValue(otherNames)) {
    return [lastName, otherNames.join(' ')];
  } return [lastName];
};

const RegisterForm = ({ done }: IProps) => {
  const { t } = useTranslation();
  // Does visitor belong to a missional community state
  const [inMc, setIsInMc] = useState(false);

  // Does visitor want to join a misional community state
  const [joinMc, setIsJoinMc] = useState(false);

  // The visitor's church name state
  const [userChurchName, setChurchName] = useState('');

  function handleSubmit(values: any, actions: FormikHelpers<any>) {
    const [lastName, middleName] = processName(values.otherNames);

    const toSave: ICreatePersonDto = {
      churchName: values.churchName,
      firstName: values.firstName,
      middleName,
      lastName,
      dateOfBirth: `1800-${values.birthMonth}-${values.birthDay}T00:00:00.000Z`,
      gender: values.gender,
      civilStatus: values.civilStatus,
      ageGroup: values.ageGroup,
      placeOfWork: values.placeOfWork,
      residence: parseGooglePlace(values.residence),
      cellGroupId: values.cellGroup?.id,
      churchLocationId: values.churchLocation.id,
      email: values.email,
      phone: values.phone,
      inCell: values.inCell,
      joinCell: values.joinCell,
    };

    post(
      remoteRoutes.register,
      toSave,
      (data) => {
        Toast.info('Registration successful. Please check your email for a link to setup a password');
        actions.resetForm();
        if (done) done();
      },
      undefined,
      () => {
        actions.setSubmitting(false);
      },
    );
  }

  // Does visitor belong to/want to join a missional community function
  const changeMCStatus = (value: any) => {
    value === 'Yes' ? setIsInMc(true) : setIsInMc(false);
    value === 'No' ? setIsJoinMc(true) : setIsJoinMc(false);
  };

  const isChurchNameValid = () => {
    if (!userChurchName) {
      Toast.error('Please enter a valid church name');
    }
  };

  const saveChurchName = (event: any) => {
    const churchName: string = event.target.value;
    if (churchName !== '') {
      setChurchName(churchName.toLowerCase().replace(/\s/g, ''));
    }
  };
  //setChurchName("conectados");
  return (
    <XForm
      onSubmit={handleSubmit}
      schema={schema}
      initialValues={initialValues}
    >
      <div style={{ padding: 8 }}>
        <Grid spacing={2} container className="min-width-100">
          <Grid item xs={12}>
            <Box pt={2}>
              <Typography variant="caption">
                {t('Basic data')}
              </Typography>
            </Box>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <XTextInput
              name="churchName"
              label={t('Church Name')}
              autoFocus
              type="text"
              variant="outlined"
              margin="none"
              onBlurCapture={saveChurchName}
              onFocus={saveChurchName} // For some reason, onBlur stops validation from immediately triggering so we use onBlurCapture
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <XTextInput
              name="firstName"
              label={t('First Name')}
              type="text"
              variant="outlined"
              margin="none"
              onBlurCapture={isChurchNameValid}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <XTextInput
              name="otherNames"
              label={t('Other Names')}
              type="text"
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box pt={1} pl={1}>
              <XRadioInput
                name="gender"
                label={t('Gender')}
                options={toOptions(genderCategories)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <XSelectInput
              name="civilStatus"
              label={t('Civil Status')}
              options={toOptions(civilStatusCategories)}
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <XSelectInput
              name="birthDay"
              label={t('Birth Day')}
              options={toOptions(getDayList())}
              variant="outlined"
              margin="none"
            />
          </Grid>          
          <Grid item xs={6} md={4}>
            <XSelectInput
              name="birthMonth"
              label={t('Birth Month')}
              options={toOptions(getMonthsList())}
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <XSelectInput
              name="ageGroup"
              label={t('Age Group')}
              options={toOptions(ageCategories)}
              variant="outlined"
              margin="none"
            />
          </Grid>          

          <Grid item xs={12}>
            <Box pt={2}>
              <Typography variant="caption">{t('Address details')}</Typography>
            </Box>
            <Divider />
          </Grid>

          <Grid item xs={12} md={6}>
            <XPhone
              name="phone"
              label={t('Phone')}
              type="text"
              placeholder="55 (41) 99999999"
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <XTextInput
              name="email"
              label={t('Email')}
              type="email"
              variant="outlined"
              margin="none"
              helperText="Será enviado um link para criar sua senha"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <XRemoteSelectLoadOnOpen
              remote={`${remoteRoutes.groupsCombo}/locations?categories[]=Localização&churchName=${userChurchName}`}
              name="churchLocation"
              label={t('Church Localization')}
              variant="outlined"
              margin="none"
              placeholder="Type to search"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <XRadioInput
                name="inCell"
                label={t('You are already part of a GC?')}
                options={toOptions(responseCategories)}
                customOnChange={changeMCStatus}
              />
            </Box>
          </Grid>
          {/* Field appears if user belongs to a Missional Community */}
          {inMc && (
            <Grid item xs={12} md={12}>
              <XRemoteSelect
                remote={`${remoteRoutes.groupsCombo}/locations?categories[]=Localização GC Curitiba e Região&churchName=${userChurchName}`}
                parser={({ name, id }: any) => ({ name, id })}
                name="cellGroup"
                label={t('Connected Group')}
                variant="outlined"
                margin="none"
                placeholder="Type to search"
              />
            </Grid>
          )}
          {/* Field appears if user does not belong to a Missional Community */}
          {joinMc && (
            <Grid item xs={12} md={12}>
              <Box>
                <XRadioInput
                  name="joinCell"
                  label={t('Do you want to join a Connected Group?')}
                  options={toOptions(responseCategories)}
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <XMapsInput
              name="residence"
              label="Endereço Residencial"
              variant="outlined"
              margin="none"
              placeholder="Type to search"
            />
            <p className='MuiFormHelperText-root MuiFormHelperText-contained'>Insira seu endereço completo, Rua, Número e Ap e selecione uma opção do Google</p>
          </Grid>
          <Grid item xs={12}>
            <XTextInput
              name="placeOfWork"
              label={t('Place of work')}
              type="text"
              variant="outlined"
              margin="none"
            />
          </Grid>
        </Grid>
      </div>
    </XForm>
  );
};

export default RegisterForm;
